import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { X, DollarSign } from 'lucide-react';
import api from '../services/api';

const CartModal = ({ isOpen, onClose, cart, goToCheckout }) => {
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    offer_amount: '',
    customer_name: '',
    customer_email: ''
  });

  // Debug: Log cart structure to console
  React.useEffect(() => {
    if (cart && cart.lineItems) {
      console.log('🛒 Cart structure:', JSON.stringify(cart, null, 2));
      console.log('🛒 First item:', cart.lineItems[0]);
    }
  }, [cart]);

  const getCartTotal = () => {
    if (!cart || !cart.lineItems) return 0;
    return cart.lineItems.reduce((total, item) => {
      // Shopify returns price as a string, need to parse it
      const price = parseFloat(item.variant?.price?.amount || item.variant?.price || 0);
      return total + price * item.quantity;
    }, 0);
  };

  const getCartSummary = () => {
    if (!cart || !cart.lineItems) return '';
    return cart.lineItems.map(item => 
      `${item.quantity}x ${item.title || item.variant?.product?.title || item.product?.title || 'Product'}`
    ).join(', ');
  };

  const handleOfferSubmit = async (e) => {
    e.preventDefault();

    if (!formData.offer_amount || parseFloat(formData.offer_amount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }

    if (!formData.customer_name.trim() || !formData.customer_email.trim()) {
      toast.error('Please provide your name and email');
      return;
    }

    try {
      setLoading(true);

      const cartTotal = getCartTotal();
      const cartSummary = getCartSummary();

      const offerData = {
        product_id: 'cart-offer',
        product_name: `Cart Offer (${cart.lineItems.length} items)`,
        product_handle: 'cart-offer',
        original_price: cartTotal,
        offer_amount: parseFloat(formData.offer_amount),
        message: `Cart offer for: ${cartSummary}`,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email
      };

      const response = await api.post('/offers/submit', offerData);

      if (response.data.success) {
        toast.success('Your cart offer has been submitted! We\'ll review it and get back to you soon.');
        setShowOfferForm(false);
        setFormData({ offer_amount: '', customer_name: '', customer_email: '' });
        onClose();
      }
    } catch (error) {
      console.error('Error submitting cart offer:', error);
      toast.error('Failed to submit offer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!cart || !cart.lineItems || cart.lineItems.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-charcoal">Your Cart</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center text-warmGray">
            Your cart is empty
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const cartTotal = getCartTotal();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-charcoal">
            Your Cart ({cart.lineItems.length} items)
          </DialogTitle>
        </DialogHeader>

        {!showOfferForm ? (
          <>
            {/* Cart Items */}
            <div className="space-y-4 max-h-60 overflow-y-auto">
              {cart.lineItems.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                  {item.variant?.image && (
                    <img 
                      src={item.variant.image.url} 
                      alt={item.variant?.product?.title || item.product?.title || 'Product'}
                      className="w-16 h-16 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-bold text-charcoal">{item.title || item.variant?.product?.title || item.product?.title || 'Product'}</h4>
                    {item.variant?.title && item.variant.title !== 'Default Title' && (
                      <p className="text-sm text-warmGray">{item.variant.title}</p>
                    )}
                    <p className="text-sm text-warmGray">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-charcoal">${((parseFloat(item.variant?.price?.amount || item.variant?.price || 0)) * item.quantity).toFixed(2)}</p>
                    <p className="text-sm text-warmGray">${parseFloat(item.variant?.price?.amount || item.variant?.price || 0).toFixed(2)} each</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Total */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center mb-6">
                <span className="text-xl font-bold text-charcoal">Total:</span>
                <span className="text-2xl font-black text-charcoal">${cartTotal.toFixed(2)}</span>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button 
                  onClick={goToCheckout}
                  className="w-full bg-charcoal text-cream hover:bg-charcoal/90 font-black text-lg py-6 uppercase tracking-wider"
                >
                  Proceed to Checkout
                </Button>
                
                <Button 
                  onClick={() => setShowOfferForm(true)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-black text-lg py-6 uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  <DollarSign className="h-6 w-6" />
                  Hook It Up (Make Offer)
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Offer Form */
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Button 
                onClick={() => setShowOfferForm(false)} 
                variant="outline" 
                size="sm"
                className="p-2"
              >
                <X className="h-4 w-4" />
              </Button>
              <h3 className="text-xl font-bold text-charcoal">Make an Offer on Your Cart</h3>
            </div>

            {/* Current Cart Total */}
            <div className="bg-gray-100 p-4 rounded mb-4">
              <p className="text-sm text-gray-600 mb-1">Cart Total ({cart.lineItems.length} items)</p>
              <p className="text-2xl font-bold text-charcoal">${cartTotal.toFixed(2)}</p>
              <p className="text-xs text-warmGray mt-1">{getCartSummary()}</p>
            </div>

            <form onSubmit={handleOfferSubmit} className="space-y-4">
              {/* Offer Amount */}
              <div>
                <Label htmlFor="offer_amount" className="text-charcoal font-bold">Your Offer Amount *</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="offer_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.offer_amount}
                    onChange={(e) => setFormData({...formData, offer_amount: e.target.value})}
                    className="pl-7"
                    required
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="customer_name" className="text-charcoal font-bold">Your Name *</Label>
                <Input
                  id="customer_name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="mt-1"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="customer_email" className="text-charcoal font-bold">Your Email *</Label>
                <Input
                  id="customer_email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  className="mt-1"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3"
              >
                {loading ? 'Submitting...' : 'Submit Cart Offer'}
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CartModal;